import { RefObject, useContext, useEffect, useRef, useState } from 'react';
import { Collapse, MenuList, Paper } from '@mui/material';
import { ServicesContext } from 'app/items/shared/context_interface';
import { ContextMenuActions } from './ContextMenuActions';
import { ContextMenuStats } from './ContextMenuStats';
import './ContextMenu.scss';
import { useMapEditingStatus } from 'app/agriculture/ContextMenu/hooks/useMapEditingStatus/useMapEditingStatus';

export interface MousePosition {
    x: number;
    y: number;
}

function isTargetInsideRef(target: EventTarget | null, ref: RefObject<HTMLUListElement>) {
    // Maybe this should be instanceof Element instead?
    return ref.current && target instanceof Node && ref.current.contains(target);
}

export const ContextMenu = () => {
    const { mapService } = useContext(ServicesContext);
    const [{ x, y }, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLUListElement>(null);
    const isMapInEditingMode = useMapEditingStatus();

    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            // don't hide the context menu if you click on the context menu
            if (isTargetInsideRef(e.target, ref)) return;
            setVisible(false);
        };

        const handleContextMenu = (e: MouseEvent) => {
            // we don't want to open the context menu on a control click
            if (e.ctrlKey) return;

            // prevent right-clicking on the context menu
            if (isTargetInsideRef(e.target, ref)) {
                e.preventDefault();
                return;
            }

            // don't show if we haven't clicked on the map
            if (e.target !== mapService.viewer.canvas) {
                setVisible(false);
                return;
            }

            e.preventDefault();
            setVisible(true);

            const { x, y } = e;
            setMousePosition({ x, y });
            return;
        };

        if (!isMapInEditingMode) {
            window.addEventListener('mousedown', handleMouseDown);
            window.addEventListener('contextmenu', handleContextMenu);

            // this tells the map to do a thing when we click it
            mapService
                .getViewer()
                .then(({ screenSpaceEventHandler }) => {
                    screenSpaceEventHandler.setInputAction(() => {
                        setVisible(false);
                    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);
                })
                .catch((e) => {
                    console.error(e);
                });
        }

        return () => {
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('contextmenu', handleContextMenu);
        };
    }, [isMapInEditingMode, mapService]);

    if (isMapInEditingMode) {
        return null;
    }

    return (
        <Collapse
            in={visible}
            sx={{ position: 'fixed', top: y, left: x, zIndex: 3 }}
            unmountOnExit
            mountOnEnter
        >
            <Paper sx={{ borderRadius: '16px' }}>
                <MenuList className="ContextMenu" ref={ref} sx={{ padding: '12px 0' }}>
                    <ContextMenuActions
                        onClick={() => {
                            setVisible(false);
                        }}
                    />
                    <ContextMenuStats mousePosition={{ x, y }} />
                </MenuList>
            </Paper>
        </Collapse>
    );
};
